<template>
  <div id="gene-tag-container" class="row-block" @mouseenter="e => mouse = true" @mouseleave="e => mouse = false">
    <div class="gene-tag-block col-block width-full">
      <input
        ref="comment-input"
        type="text"
        :disabled="disabled"
        :placeholder="placeholder"
        v-model="content"
        @focus="e => focus = true"
        @blur="e => focus = false"
        @keydown.up.prevent.stop="e => moveTagIndex('up')"
        @keydown.down.prevent.stop="e => moveTagIndex('down')"
        @keydown.enter.prevent.stop="e => genes.length <= 1 ? $emit('send', content) : commentTag()"
        @input="e => commentOnInput(true)"
        @change="e => $emit('change', content)"
      >
      <div v-if="needFilter" class="gene-tag-filter-field block col-block">
        <div class="gene-tag-filter-container block col-block">
          <div class="block-content">
            <div class="display-icon center-block" :class="{ active: displayTagFilter }" @click="e => displayTagFilter = !displayTagFilter">
              <img :src="require('../../assets/images/case-edit-display-icon.svg')" alt="">
            </div>
            <div class="block-title-container row-block">
              <div class="block-title" :style="!displayTagFilter ? { color: '#777777' } : {}">{{ $t('COMPONENT.tool.geneTag.___filterTitle') + (!displayTagFilter ? (': ' + currentFilter.title.toLowerCase()) : '') }}</div>
              <div :style="{ flex: 1 }"></div>
            </div>
            <div v-show="displayTagFilter" class="gene-tag-filter-block block-content col-block width-full">
              <div class="gene-tag-filter-content-list-container row-block flex-wrap">
                <div
                  class="gene-tag-filter-content-container row-block"
                  v-for="(option, oindex) in filterOptions"
                  :key="oindex"
                  @click="e => (filter = option.value)"
                >
                  <img :src="filter === option.value ? require('../../assets/images/radio-true.svg') : require('../../assets/images/radio-false.svg')" alt="">
                  <div class="gene-tag-filter-content-title">{{ option.title }}</div>
                </div>
                <div :style="{ flex: 1 }"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="input-tag" :style="tagWidth ? { 'min-width': tagWidth } : {}" v-if="genes && !onCompelete && display && content">
      <div class="tag-block">
        <template v-if="typeList.reduce((count, type) => count + (filtedGenes[type] || []).length, 0) > 0">
          <div v-if="dataInfo && (typeList.some(type => dataInfo[type]))" class="tag-info-container">
            <div class="tag-info-block">
              <span v-html="typeList.map(type => ((dataInfo[type] || {}).displayed ? (dataInfo[type].displayed + '/' + dataInfo[type].total + ' ' + $t('COMPONENT.tool.geneTag.info.___' + type)) : '')).filter(x => x).join(', ').replace(/,(?!.*,)/, ', ' + $t('COMPONENT.tool.geneTag.info.___and')) + '.'"></span>
            </div>
          </div>
          <div ref="tag-block" class="tag-group-container" :style="tagHeight ? { 'max-height': tagHeight } : {}">
            <div class="tag-group-block" v-for="(type, tindex) in typeList.filter(type => (filtedGenes[type] || []).length)" :key="tindex">
              <div v-if="title" class="tag-title">{{ $t('COMPONENT.tool.geneTag.title.___' + type) }}</div>
              <div
                class="tag"
                v-for="(tag, index) in filtedGenes[type]"
                :key="index"
                :class="{ selected: tagIndex === (index + typeList.slice(0, typeList.indexOf(type)).reduce((value, type) => value + (filtedGenes[type] || []).length, 0)) }"
                @mousemove="e => tagIndex = (index + typeList.slice(0, typeList.indexOf(type)).reduce((value, type) => value + (filtedGenes[type] || []).length, 0))"
                @click="commentTag()"
              >
                <span v-html="tag.name.replace(new RegExp(content, 'ig'), m => '<span class=\'key\'>' + m + '</span>')"></span>
                <span v-if="tag.gene_count" v-html="'<span style=color:#FA3D3D> (' + tag.gene_count + ' gene' + (tag.gene_count > 1 ? 's' : '') + ')</span>'"></span>
              </div>
            </div>
          </div>
        </template>
        <template v-else>
          <div class="tab-empty">{{ $t('COMPONENT.tool.geneTag.___empty') }}</div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { groupBy } from '../../constants/global/util'

export default {
  props: {
    disabled: {
      type: Boolean,
      default: () => false
    },
    init: {
      type: String,
      default: () => ''
    },
    tagWidth: {
      type: String,
      default: () => ''
    },
    tagHeight: {
      type: String,
      default: () => ''
    },
    title: {
      type: Boolean,
      default: () => true
    },
    dataInfo: {
      type: Object,
      default: () => null
    },
    genes: {
      type: Array,
      default: () => null
    },
    placeholder: {
      type: String,
      default: () => ''
    },
    noFocus: {
      type: Boolean,
      default: false
    },
    needFilter: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      content: '',
      tagIndex: 0,
      filter: 'all',
      onCompelete: false,
      focus: false,
      mouse: false,
      display: false,
      displayTagFilter: false
    }
  },
  computed: {
    filtedGenes () {
      return groupBy(this.genes, gene => gene.type, true)
    },
    currentGenes () {
      return this.typeList.reduce((result, type) => [...result, ...(this.filtedGenes[type] || [])], [])
    },
    currentFilter () {
      return this.filterOptions.find(option => option.value === this.filter)
    },
    // static
    typeList () {
      return (this.filter === 'all') ? ['gene', 'panel', 'hpo', 'condition', 'pathway'] : [this.filter]
    },
    filterOptions () {
      return [
        {
          title: this.$t('COMPONENT.tool.geneTag.title.___all'),
          value: 'all'
        },
        {
          title: this.$t('COMPONENT.tool.geneTag.title.___gene'),
          value: 'gene'
        },
        {
          title: this.$t('COMPONENT.tool.geneTag.title.___panel'),
          value: 'panel'
        },
        {
          title: this.$t('COMPONENT.tool.geneTag.title.___condition'),
          value: 'condition'
        },
        {
          title: this.$t('COMPONENT.tool.geneTag.title.___pathway'),
          value: 'pathway'
        }
      ]
    }
  },
  watch: {
    'init' () {
      if (this.content !== this.init) {
        this.content = this.init
      }
    },
    'focus' () {
      this.display = this.focus || this.mouse
    }
  },
  created () {
    if (this.content !== this.init) {
      this.content = this.init
    }
  },
  methods: {
    commentOnInput (compelete) {
      compelete && (this.onCompelete = false)
      this.tagIndex = 0
      this.$emit('input', this.content)
    },
    moveTagIndex (type) {
      switch (type) {
        case 'up': {
          this.tagIndex = (this.tagIndex - -this.currentGenes.length - 1) % this.currentGenes.length
          break
        }
        case 'down': {
          this.tagIndex = (this.tagIndex - -1) % this.currentGenes.length
          break
        }
      }
      this.$refs['tag-block'].scrollTop = 28 * this.tagIndex
    },
    commentTag () {
      const curTag = this.currentGenes[this.tagIndex]
      this.onCompelete = true
      this.content = curTag.name
      this.$emit('tag', curTag)
      this.commentOnInput()
      !this.noFocus && this.$refs['comment-input'].focus()
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="sass">
#gene-tag-container
  position: relative
  .gene-tag-block
    width: 100%
    input
      width: 100%
      &:hover
        & + .description-tag
          .info-container
            opacity: 1
            top: 58px
      &:focus
        & + .description-tag
          .info-container
            opacity: 0
            top: -9999999999999999px
    .gene-tag-filter-field
      padding: 0 !important
      border-radius: 0
      border: 0
      .gene-tag-filter-container
        padding: 7px
        .block-title-container
          min-height: 10px
        .block-title
          min-height: 10px
        .block-content
          min-height: 10px
        .display-icon
        .gene-tag-filter-block
          margin-top: 7px
          gap: 20px
          .gene-tag-filter-content-list-container
            gap: 5px 20px
            .gene-tag-filter-content-container
              gap: 10px
              cursor: pointer
              .gene-tag-filter-content-title
                color: #003679
  .input-tag
    position: absolute
    width: 100%
    top: 36px
    overflow: hidden
    z-index: 5
    .tag-block
      width: 100%
      box-sizing: border-box
      padding: 5px 0
      background: white
      border-radius: 3px
      border: 1px solid #003679
      z-index: 100
      .tag-info-container
        width: 100%
        padding: 0 5px
        margin-bottom: 7px
        box-sizing: border-box
        .tag-info-block
          width: 100%
          padding: 3px 5px
          box-sizing: border-box
          background: #F8FAFA
          border-radius: 3px
          color: #003679
      .tag-group-container
        overflow-y: auto
        max-height: 400px
        .tag-group-block
          & > div
            width: 100%
          &:not(:last-child)
            margin-bottom: 8px
          .tag-title
            color: #003679
            margin-bottom: 7px
            padding: 0 10px
            font-weight: 500
            box-sizing: border-box
          .tag
            padding: 3px 10px
            cursor: pointer
            user-select: none
            box-sizing: border-box
            word-break: break-word
            &.selected
              background: #003679
              color: white
              .key
                color: white
            span
              white-space: pre
            .key
              color: #003679
      .tab-empty
        color: #999999
        font-weight: 300
        padding: 0 10px
</style>



// WEBPACK FOOTER //
// src/components/tool/geneTag.vue